import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Avatar, Button, Grid } from "~/components-shared";
import { useDispatch, useSelector } from "react-redux";
import requestCommentSelectors from "~/store/requestComments/selectors";
import { AIGenerateTargetEnum, ImageCategoryEnum, StatusStateEnum } from "~/types/enums";
import requestCommentsActionCreators from "~/store/requestComments/actions";
import authSelectors from "~/store/auth/selectors";
import InputMarkdownTextArea from "~/components-shared/InputMarkdownTextArea";
import AiGenerateButton from "~/components/AiGenerateButton";
import { dataURLtoFile } from "~/lib/helpers";
import api from "~/lib/api";
import styled, { useTheme } from "styled-components";
import Picture from "../../components-shared/Picture";
import Icon from "../../components-shared/Icon";
const ImageContent = styled.div`
  position: relative;
  width: 320px;
  height: 320px;
  margin-bottom: ${_ref => {
  let {
    theme
  } = _ref;
  return 3 / (theme.typography.fontSize / theme.spacing.step);
}}rem;
`;
const RemoveButton = styled(Button)`
  position: absolute;
  top: 10px;
  right: 10px;
`;
const RequestCommentCreate = _ref2 => {
  let {
    requestId
  } = _ref2;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const status = useSelector(requestCommentSelectors.getStatusByRequest(requestId));
  const createStatus = useSelector(requestCommentSelectors.getStatusByRequestComment("+"));
  const user = useSelector(authSelectors.getUser);
  const [commentText, setCommentText] = useState("");
  const [commentImage, setCommentImage] = useState("");
  const [commentImageKey, setCommentImageKey] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const [error, setError] = useState(undefined);
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(requestCommentsActionCreators.fetchRequestStarted(requestId));
    }
  }, [dispatch, requestId, status]);
  const handleSubmit = () => {
    dispatch(requestCommentsActionCreators.createStarted(requestId, {
      requestId,
      text: commentText,
      image: commentImageKey
    }, data => {
      if (data) {
        setCommentText("");
        setCommentImage("");
        setCommentImageKey("");
      }
    }));
  };
  const uploadAiImage = async base64 => {
    if (base64) {
      try {
        setError(undefined);
        setImageLoading(true);
        const file = dataURLtoFile(`data:image/jpeg;base64,${base64}`, "ai_generated");
        const uploadedImage = await api.uploadImage(file, ImageCategoryEnum.REQUEST_COMMENT);
        setCommentImage(uploadedImage.url);
        setCommentImageKey(uploadedImage.key);
      } catch (err) {
        setError(err.toString());
      } finally {
        setImageLoading(false);
      }
    }
  };
  return /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 5,
    template: "3.6rem 1fr",
    justify: "start",
    s: {
      mb: 6
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Avatar, {
    size: 12.5,
    user: user,
    showStatus: true,
    showEmoji: true
  }), /*#__PURE__*/React.createElement(Grid.Cell, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), (commentImage || imageLoading) && /*#__PURE__*/React.createElement(ImageContent, null, /*#__PURE__*/React.createElement(Picture, {
    src: commentImage,
    color: colors.grey.main,
    width: "320px",
    height: "320px",
    loading: imageLoading
  }), /*#__PURE__*/React.createElement(RemoveButton, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("common.actions.remove"),
    onClick: () => setCommentImage(""),
    color: "translucent",
    rounded: true,
    s: {
      p: 2,
      mx: 0.5
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    src: "cross",
    color: colors.text.primary,
    display: "block"
  }))), /*#__PURE__*/React.createElement(InputMarkdownTextArea, {
    name: "text",
    s: {
      mb: 3
    },
    value: commentText,
    onChange: setCommentText
  }), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSubmit,
    loading: createStatus === StatusStateEnum.LOADING,
    color: "secondary",
    s: {
      px: 8,
      py: 2
    },
    rounded: true,
    display: "inline-block"
  }, t("common.actions.send")), /*#__PURE__*/React.createElement(AiGenerateButton, {
    s: {
      pl: 4,
      py: 2,
      pr: 6,
      ml: 3
    },
    display: "inline-block",
    fontSize: 4,
    target: AIGenerateTargetEnum.REQUEST_COMMENT,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("RequestCommentsCard.generateImageTip"),
    value: commentText,
    noValueError: t("RequestCommentsCard.generateImageError"),
    onGenerateStarted: () => setError(undefined),
    onGenerateSucceeded: file => uploadAiImage(file[0]?.b64_json),
    onGenerateFailed: message => setError(message)
  }, t("RequestCommentsCard.generateImage"))));
};
export default RequestCommentCreate;